<script>
  import LinkConfigurationItem from './LinkConfigurationItem.svelte';
  
  export let question;
  export let updateWorkingQuestion;
  export let questionIndex;
  
  let selectedLinkIndex = 0;
  let allLinks;
  
  function getAllLinks() {
    const linksHandler = question.dataset?.find(handler => handler[0] === 'links');
    if (!linksHandler || !linksHandler[1] || !Array.isArray(linksHandler[1])) {
      return [];
    }
    return linksHandler[1];
  }
  
  function addNewLink() {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      links.push({
        source: '__self__',
        target: '__answers__',
        type: 'link-type'
      });
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
      selectedLinkIndex = links.length - 1;
    } else {
      // Create new links handler if none exists
      const newDataset = [...question.dataset];
      newDataset.push(['links', [{
        source: '__self__',
        target: '__answers__',
        type: 'link-type'
      }]]);
      updateWorkingQuestion('dataset', newDataset);
      selectedLinkIndex = 0;
    }
  }
  
  function removeLink(index) {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      
      if (links.length > 1) {
        links.splice(index, 1);
        handler[1] = links;
        dataset[linksHandlerIndex] = handler;
        updateWorkingQuestion('dataset', dataset);
        
        if (selectedLinkIndex >= links.length) {
          selectedLinkIndex = links.length - 1;
        }
      } else {
        // If this is the last link, remove the entire links handler
        dataset.splice(linksHandlerIndex, 1);
        updateWorkingQuestion('dataset', dataset);
        selectedLinkIndex = 0;
      }
    }
  }
  
  function duplicateLink(index) {
    const dataset = [...question.dataset];
    const linksHandlerIndex = dataset.findIndex(handler => handler[0] === 'links');
    
    if (linksHandlerIndex !== -1) {
      const handler = [...dataset[linksHandlerIndex]];
      const links = [...handler[1]];
      const linkToDuplicate = { ...links[index] };
      links.splice(index + 1, 0, linkToDuplicate);
      handler[1] = links;
      dataset[linksHandlerIndex] = handler;
      updateWorkingQuestion('dataset', dataset);
      selectedLinkIndex = index + 1;
    }
  }
  
  function updateSelectedLinkIndex(newIndex) {
    selectedLinkIndex = newIndex;
  }
  
  // Reactive derived state - automatically updates when question changes
  $: question, allLinks = getAllLinks();
  $: hasLinks = allLinks.length > 0;
</script>

{#if hasLinks}
  <div class="link-editor-section">
    <h5>
      Link Configuration
      <span class="link-count-badge">
        {allLinks.length} link{allLinks.length !== 1 ? 's' : ''}
      </span>
    </h5>
    
    <div class="links-container">
      {#each allLinks as link, linkIndex}
        <LinkConfigurationItem 
          {link}
          {linkIndex}
          selectedLinkIndex={selectedLinkIndex}
          {updateWorkingQuestion}
          {question}
          {questionIndex}
          on:remove={() => removeLink(linkIndex)}
          on:duplicate={() => duplicateLink(linkIndex)}
          on:selectLink={() => updateSelectedLinkIndex(linkIndex)}
        />
      {/each}
    </div>
    
    <div class="link-actions">
      <button class="btn btn-primary" on:click={addNewLink}>
        Add New Link Configuration
      </button>
    </div>
    
    <p class="field-help">
      Create multiple link types with different sources, targets, and properties. 
      Links define how nodes in your network are connected.
    </p>
  </div>
{:else}
  <div class="no-links-section">
    <h5>Link Configuration</h5>
    <div class="no-links-content">
      <p class="no-links-text">No link configurations defined</p>
      <p class="no-links-description">
        Links define relationships between nodes in your network. Add a link configuration 
        to connect this question's nodes to other nodes.
      </p>
      <button class="btn btn-primary" on:click={addNewLink}>
        Add First Link Configuration
      </button>
    </div>
  </div>
{/if}

<style lang="scss">
  .link-editor-section {
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 20px;
    background: rgba(255, 255, 255, 0.7);
    
    h5 {
      margin-bottom: 15px;
      font-weight: bold;
      font-size: 0.85rem;
      color: #374151;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  
  .no-links-section {
    border: 2px dashed #d1d5db;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    background: #f9fafb;
    text-align: center;
    
    h5 {
      margin-bottom: 15px;
      font-weight: bold;
      font-size: 0.85rem;
      color: #374151;
    }
  }
  
  .no-links-content {
    .no-links-text {
      font-size: 0.9rem;
      color: #6b7280;
      margin-bottom: 8px;
      font-weight: 500;
    }
    
    .no-links-description {
      font-size: 0.75rem;
      color: #9ca3af;
      margin-bottom: 15px;
      font-style: italic;
      line-height: 1.4;
    }
  }
  
  .link-count-badge {
    background: #3b82f6;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  
  .links-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 15px;
  }
  
  .link-actions {
    padding-top: 15px;
    border-top: 1px solid #e5e7eb;
    text-align: center;
  }
  
  .field-help {
    font-size: 11px;
    color: #6b7280;
    margin-top: 10px;
    font-style: italic;
    text-align: center;
  }
  
  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #3b82f6;
      color: white;
      
      &:hover {
        background: #2563eb;
      }
    }
  }
</style>